import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import useAppMethods from '../utility/useAppMethods';
import { useCallback, useContext, useMemo } from 'react';
import { CompanyDetailsInterface, ProductInterface } from '../../services/exports/Interfaces';
import { MenuContext } from '../../providers/MenuProvider';
import { CategoryType } from '../../services/exports/Constants';
import collect from 'collect.js';

interface Props {
  company: CompanyDetailsInterface;
}

export const REDEMPTION_STEP = 10;
// Mirrors CashbackAccount::POINTS_TO_CENTS in shop-backend: 1 point == 10 minor units of the
// company's currency, whatever that currency is. Nothing here is euro-specific.
export const POINTS_PER_CURRENCY_UNIT = 10;
export const MAX_EXTRA_REDEMPTION_OPTIONS = 3;

export default function useCashbackProgram(props?: Props) {
  const { company: defaultCompany, cashback_account } = useSelector((store: StoreInterface) => store.initialData);
  const company = props?.company ?? defaultCompany;

  const { formatCurrency } = useAppMethods();

  const balance = cashback_account?.balance ?? 0;
  const percentage = company?.cashback_program?.percentage ?? 0;
  const multiplier = percentage / 100;

  const pointsToMoney = useCallback((points: number): number => points / POINTS_PER_CURRENCY_UNIT, []);

  const formatPointsAsMoney = useCallback(
    (value: number): string | number => formatCurrency(pointsToMoney(value)),
    [formatCurrency, pointsToMoney],
  );

  const formattedBalance = balance > 0 ? formatPointsAsMoney(balance) : null;

  const menu = useContext(MenuContext);
  const allCategories = menu?.allCategories;

  const rewardProducts = useMemo<ProductInterface[]>(() => {
    const mostPopular = collect(allCategories ?? [])
      .where('type', CategoryType.MostPopular)
      .first() as { products?: ProductInterface[] } | undefined;

    if (!mostPopular?.products) return [];

    return collect(mostPopular.products).sortBy('base_price').take(3).toArray() as ProductInterface[];
  }, [allCategories]);

  const maxSpendingLimit = company?.cashback_program?.max_spending_limit ?? 0;

  const freeRewardProduct = useMemo<ProductInterface | null>(() => {
    if (maxSpendingLimit <= 0) return null;

    const maxOff = pointsToMoney(maxSpendingLimit);

    return rewardProducts.find((product) => product.base_price <= maxOff) ?? null;
  }, [rewardProducts, maxSpendingLimit, pointsToMoney]);

  // Generates the list of point amounts the customer can pick to redeem on their order.
  // Guarantees every option satisfies the backend's CreateOrderRequest validation:
  //   value >= min_spending_limit  &&  value <= max_spending_limit  &&  value <= balance.
  // Also caps options at the order subtotal (in points) so the UI never suggests more points than
  // the order can absorb — the backend discount processor caps the redeemed amount anyway, but the
  // UX should match.
  const getRedemptionOptions = useCallback(
    (subtotal: number): number[] => {
      if (!company?.cashback_program) return [];

      const min = company.cashback_program.min_spending_limit;
      const max = company.cashback_program.max_spending_limit;
      const subtotalInPoints = Math.floor(subtotal * 10);
      const cap = Math.min(balance, max, subtotalInPoints);

      if (cap < min) {
        return [];
      }

      // Top option: round cap down to the step, but never below min — since cap >= min, picking
      // min here is always backend-valid.
      const flooredTop = Math.floor(cap / REDEMPTION_STEP) * REDEMPTION_STEP;
      const top = Math.max(flooredTop, min);

      const result: number[] = [];

      for (let i = 0; i <= MAX_EXTRA_REDEMPTION_OPTIONS; i++) {
        const value = top - i * REDEMPTION_STEP;

        if (value > min) {
          result.push(value);
        } else {
          break;
        }
      }

      if (!result.includes(min)) {
        result.push(min);
      }

      return result.sort((a, b) => a - b);
    },
    [balance, company?.cashback_program],
  );

  // Mirrors backend OrderCashbackService::calculatePoints():
  //   points = round(amount_in_cents * percentage_multiplier / 10, half_up)
  // PHP_ROUND_HALF_UP rounds halves away from zero — Math.floor(|x| + 0.5) reproduces that exactly.
  const calculateExpectedCashback = useCallback(
    (amount: number): number => {
      const points = amount * 10 * multiplier;

      return Math.sign(points) * Math.floor(Math.abs(points) + 0.5);
    },
    [multiplier],
  );

  const calculateMinAmountForExpectedCashback = useCallback(
    (cashbackAmount: number): number => (multiplier > 0 ? cashbackAmount / multiplier : 0),
    [multiplier],
  );

  // Human-readable earn rate.
  // - When the program grants ≥ 1 pt per 1 currency unit: { count: pts/unit, amountPerUnit: 1 } → "N points per 1"
  // - When it grants < 1 pt per unit:                     { count: 1, amountPerUnit: units/pt } → "1 point per X"
  // Computed from the per-100-units rate so half-up rounding on small percentages doesn't lie about the per-unit rate.
  const earnRate = useMemo<{ count: number; amountPerUnit: number }>(() => {
    const pointsPer100 = calculateExpectedCashback(100);

    if (pointsPer100 <= 0) {
      return { count: 1, amountPerUnit: 1 };
    }

    if (pointsPer100 >= 100) {
      return { count: calculateExpectedCashback(1), amountPerUnit: 1 };
    }

    return { count: 1, amountPerUnit: Math.max(1, Math.round(100 / pointsPer100)) };
  }, [calculateExpectedCashback]);

  // Largest amount of points the user can redeem on an order with the given subtotal.
  // Returns 0 if the cap (min of balance & subtotal-in-points) is below the program's min_spending_limit
  // — regardless of which side is the limiting factor — so callers never see an invalid in-between value.
  const calculateSpendableCashback = useCallback(
    (amount: number): number => {
      if (!company?.cashback_program) return 0;

      const { min_spending_limit: min, max_spending_limit: max } = company.cashback_program;
      const pointAmount = Math.floor(amount * 10);
      const cap = Math.min(balance, pointAmount);

      if (cap < min) {
        return 0;
      }

      if (cap > max) {
        return max;
      }

      return cap;
    },
    [balance, company?.cashback_program],
  );

  return {
    hasCashbackProgram: !!company?.cashback_program,
    cashbackProgram: company?.cashback_program,
    account: cashback_account,
    percentage,
    balance,
    formattedBalance,
    calculateExpectedCashback,
    calculateSpendableCashback,
    calculateMinAmountForExpectedCashback,
    getRedemptionOptions,
    formatPointsAsMoney,
    pointsToMoney,
    rewardProducts,
    freeRewardProduct,
    earnRate,
  };
}
